import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { ArrowLeft, ArrowUpRight, Star, Crown, Clapperboard, Sparkles, Mic2, Instagram, Quote } from "lucide-react";
import FloatingBookButton from "../components/FloatingBookButton";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/fdlrkebv_WhatsApp%20Image%202025-11-21%20at%2012.44.06_ff66975e.jpg";

// Masked line reveal
const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const CLIENTS = [
  {
    name: "Luyanda Zwane",
    role: "Actress · Model · Influencer",
    icon: Clapperboard,
    image: "/assets/clients/luyanda-zwane.jpg",
    alt: "Luyanda Zwane - Johannesburg locs client",
    instagram: "luyanda__zwane",
    bio: "Rising star of South African television, Luyanda captured national attention as Sibongile Mbambo in the hit telenovela Sibongile & The Dlaminis. Her screen journey spans Shaka iLembe, Outlaws, BET's Redemption and Netflix's Miseducation — marking her as one of the country's most watched young talents.",
    connection: "Her signature crown of locs has become part of her on-screen identity — a look she entrusts to the Carlocs standard of care.",
    quote: "My crown is part of every character I play. Carlocs keeps it flawless — on screen and off.",
  },
  {
    name: "Jaykatana",
    role: "Hip-Hop · Trap Artist",
    icon: Mic2,
    image: "/assets/clients/jaykatana.jpg",
    alt: "Jaykatana - Johannesburg locs client",
    instagram: "jaykatana",
    bio: "Sandton's own trap prodigy, Jaykatana broke through with the hit Eish before his debut album The World Is Yours claimed the number one spot on Apple Music in South Africa and Botswana. His aggressive sound and unfiltered style have made him a fixture of the new wave.",
    connection: "His unmistakable locs are part of the rockstar image — shaped and maintained with the precision Carlocs is known for.",
    quote: "The locs are part of the image. One chair, one standard — Carlocs every time.",
  },
  {
    name: "The Bomb RSA",
    role: "Influencer · Model · Content Creator",
    icon: Sparkles,
    image: "/assets/clients/the-bomb-rsa.jpg",
    alt: "The Bomb RSA - Johannesburg locs client",
    instagram: "thebomb.rsa",
    bio: "Known to over a million followers as The Bomb RSA, Andiswa Selepe built one of South Africa's most influential digital platforms through fashion, beauty and lifestyle content. A defining face of the country's creator generation, her look sets trends far beyond her feed.",
    connection: "For a creator whose image is her craft, loc maintenance is non-negotiable — and Carlocs is part of that ritual.",
    quote: "A million eyes on my look means I only trust one name with my locs. Carlocs never misses.",
  },
  {
    name: "Relebohile",
    role: "Style Muse · Trendsetter",
    icon: Crown,
    image: "/assets/clients/mpopii-doll.jpg",
    alt: "Relebohile - Johannesburg locs client",
    instagram: "mpopii.doll",
    bio: "Effortlessly stylish and never afraid of a bold new look, Relebohile is the definition of a modern muse — always polished, always turning heads. Her love for standout hair artistry means every visit ends in a fresh statement.",
    connection: "Her love affair with unforgettable styles leads back to one chair — the Carlocs chair, where every crown is crafted to turn heads.",
    quote: "Every style tells a story, and mine always begin in the Carlocs chair.",
  },
  {
    name: "King Ginger",
    role: "YouTuber · Media Personality",
    icon: Clapperboard,
    image: "/assets/clients/king-ginger.jpg",
    alt: "King Ginger - Johannesburg locs client",
    instagram: "kingginger_sa",
    bio: "One of South Africa's most entertaining digital creators, King Ginger is the creator and host of the viral street-interview show Niyathembana Na. His high-energy persona, humour and bold street-style content have built him a loyal following far beyond YouTube.",
    connection: "A bold personality needs a crown to match — his look is kept sharp within the Carlocs circle.",
    quote: "When the cameras are always rolling, your crown can't miss. Carlocs keeps me ready.",
  },
];

const ClientCard = ({ client, index }) => {
  const reversed = index % 2 === 1;
  const Icon = client.icon;
  return (
    <Reveal className="w-full">
      <article
        data-testid={`client-card-${client.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center"
      >
        {/* Image */}
        <div className={`md:col-span-5 ${reversed ? "md:order-2" : ""}`}>
          <div className="relative">
            {/* Gold offset frame */}
            <div className="absolute -inset-3 border border-[#D4AF37]/25 rounded-2xl translate-x-3 translate-y-3 transition-transform duration-700 group-hover:translate-x-1.5 group-hover:translate-y-1.5" />
            <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-[#0d0d0d]">
              <img
                src={client.image}
                alt={client.alt}
                data-testid={`client-image-${index}`}
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-700" />
            </div>
          </div>
        </div>

        {/* Copy */}
        <div className={`md:col-span-7 ${reversed ? "md:order-1 md:text-right md:pr-4" : "md:pl-4"}`}>
          <div className={`flex items-center gap-3 mb-4 ${reversed ? "md:justify-end" : ""}`}>
            <span className="font-serif text-5xl md:text-6xl text-[#D4AF37]/20 leading-none select-none">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="h-px flex-1 max-w-16 bg-[#D4AF37]/30" />
            <Icon className="w-5 h-5 text-[#D4AF37]" />
          </div>

          <h2
            data-testid={`client-name-${index}`}
            className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mb-3"
          >
            {client.name}
          </h2>
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.35em] mb-6">{client.role}</p>

          <p className="text-white/65 text-base md:text-lg font-light leading-relaxed mb-6 max-w-xl md:max-w-none inline-block">
            {client.bio}
          </p>

          <div className={`border-l-2 border-[#D4AF37]/50 pl-5 py-1 ${reversed ? "md:border-l-0 md:border-r-2 md:pl-0 md:pr-5" : ""}`}>
            <p className="text-white/45 text-sm md:text-base font-light italic leading-relaxed max-w-xl md:max-w-none inline-block">
              {client.connection}
            </p>
          </div>

          {/* Personal Quote */}
          {client.quote && (
            <blockquote
              data-testid={`client-quote-${index}`}
              className={`mt-7 flex items-start gap-4 ${reversed ? "md:flex-row-reverse md:text-right" : ""}`}
            >
              <Quote className="w-7 h-7 text-[#D4AF37]/60 shrink-0 mt-1" />
              <p className="font-serif italic text-lg md:text-xl text-white/85 leading-relaxed max-w-xl">
                "{client.quote}"
              </p>
            </blockquote>
          )}

          {client.instagram && (
            <a
              href={`https://www.instagram.com/${client.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`client-instagram-${index}`}
              className={`group/ig inline-flex items-center gap-2.5 mt-7 border border-white/15 rounded-full px-5 py-2.5 text-xs uppercase tracking-widest text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 ${reversed ? "md:ml-auto" : ""}`}
            >
              <Instagram className="w-4 h-4" />
              @{client.instagram}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/ig:translate-x-0.5 group-hover/ig:-translate-y-0.5" />
            </a>
          )}
        </div>
      </article>
    </Reveal>
  );
};

const NotableClientsPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "Notable Clients | Loctician Carlocs";
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    const id = requestAnimationFrame(raf);
    window.scrollTo(0, 0);
    return () => { cancelAnimationFrame(id); lenis.destroy(); document.title = "Loctician Carlocs | Luxury Loc Care in South Africa"; };
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div className="bg-[#050505] text-white overflow-x-hidden" data-testid="notable-clients-page">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-11 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="nav-home-link"
            className="flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.08),transparent_55%)]" />
          <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
        </div>

        <motion.div style={{ opacity: heroOpacity, y: heroY }} className="max-w-7xl mx-auto px-6 pt-36 pb-20 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-[#D4AF37] text-xs uppercase tracking-[0.4em] mb-8 flex items-center gap-3"
          >
            <Star className="w-4 h-4" /> The Carlocs Circle
          </motion.p>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            <MaskLine delay={0.3}>Notable</MaskLine>
            <MaskLine delay={0.45}><span className="italic text-[#D4AF37]">Clients.</span></MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="text-white/60 text-base md:text-lg font-light max-w-xl leading-relaxed"
          >
            Screens, stages and feeds across South Africa — some of the country's most recognisable
            crowns are cared for within these walls. A quiet honour we hold with pride.
          </motion.p>
        </motion.div>
      </section>

      {/* CLIENTS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 space-y-24 md:space-y-36">
          {CLIENTS.map((client, i) => (
            <React.Fragment key={client.name}>
              <ClientCard client={client} index={i} />
              {i < CLIENTS.length - 1 && (
                <div className="flex items-center gap-4 pt-4">
                  <span className="h-px flex-1 bg-white/5" />
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37]/40" />
                  <span className="h-px flex-1 bg-white/5" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(212,175,55,0.06),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <Star className="w-6 h-6 text-[#D4AF37] mx-auto mb-8" />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-8">
              Every crown tells <span className="italic text-[#D4AF37]">a story.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/50 text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10">
              From rising stars to household names, the trust placed in our craft is the true
              measure of the Carlocs standard — discretion, precision and care, every single time.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/"
              data-testid="closing-home-link"
              className="group inline-flex items-center justify-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] px-10 py-5 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#050505] transition-all duration-300 hover:-translate-y-1"
            >
              Discover Loctician Carlocs
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/"><img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-10 w-auto" /></Link>
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} Loctician Carlocs. All rights reserved.</p>
          <Link to="/" className="text-white/50 hover:text-[#D4AF37] transition-colors text-sm">Return to Main Site</Link>
        </div>
      </footer>

      <FloatingBookButton />
    </div>
  );
};

export default NotableClientsPage;
